import * as React from "react";
import { useLocale } from "@/contexts/LocaleContext";
import { t as translate, type Locale } from "@/lib/i18n";
import { MARKETS, type MarketId } from "@/lib/markets";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PhoneInputProps {
  /** القيمة بصيغة E.164 (مثلاً "+971501234567") أو فارغة */
  value: string;
  /** يستقبل دائماً E.164 كاملاً، أو فارغاً إن لم يُدخل رقم */
  onChange: (e164: string) => void;
  className?: string;
}

// Shipping expansion does not change account phone validation rules.
const DIAL_OPTIONS: MarketId[] = ["SA", "AE", "KW", "QA", "BH", "OM", "IQ", "US"];

/** الطول المتوقَّع للرقم المحلّي (بدون رمز الدولة وبدون صفر بادئ) */
export const NATIONAL_LENGTHS: Partial<Record<MarketId, number>> = {
  SA: 9, // 5XXXXXXXX
  AE: 9, // 5XXXXXXXX
  KW: 8, // XXXXXXXX
  QA: 8, // XXXXXXXX
  BH: 8, // XXXXXXXX
  IQ: 10, // 7XXXXXXXXX
  OM: 8, // 9XXXXXXX
  US: 10, // NXXNXXXXXX
  ROW: 0,
};

/** أمثلة على شكل الرقم المحلّي لكلّ سوق (placeholder فقط، بصفر بادئ مألوف للعميل) */
const EXAMPLES: Partial<Record<MarketId, string>> = {
  SA: "05X XXX XXXX",
  AE: "05X XXX XXXX",
  KW: "XXXX XXXX",
  QA: "XXXX XXXX",
  BH: "XXXX XXXX",
  IQ: "07XX XXX XXXX",
  OM: "09XX XXXX",
  US: "(XXX) XXX-XXXX",
  ROW: "",
};

/**
 * يتحقّق من صحّة E.164 لسوق معيّن. يُرجع رسالة خطأ مترجَمة عبر t() أو null.
 * يقبل أيضاً boolean (isAr) للتوافق مع الاستدعاءات القديمة.
 */
export function validateE164(e164: string, localeOrIsAr: Locale | boolean): string | null {
  const locale: Locale =
    typeof localeOrIsAr === "boolean" ? (localeOrIsAr ? "ar" : "en") : localeOrIsAr;
  const v = (e164 || "").trim();
  if (!v) return translate(locale, "phone.error.empty");
  if (!/^\+\d{6,16}$/.test(v)) {
    return translate(locale, "phone.error.invalid_format");
  }
  for (const id of DIAL_OPTIONS) {
    const dc = MARKETS[id].dialCode;
    if (!dc) continue;
    if (v.startsWith("+" + dc)) {
      const nat = v.slice(dc.length + 1);
      const expected = NATIONAL_LENGTHS[id] ?? 0;
      if (expected > 0 && nat.length !== expected) {
        const countryLabel =
          (MARKETS[id].labels as Record<string, string>)[locale] ??
          MARKETS[id].labels.en ??
          id;
        return translate(locale, "phone.error.national_length", {
          country: countryLabel,
          n: String(expected),
        });
      }
      return null;
    }
  }
  return translate(locale, "phone.error.country_unsupported");
}


/** يحلّل قيمة E.164 الواردة → (dial, national) */
export
function parseE164(v: string): { dial: string; national: string } {
  const trimmed = (v || "").replace(/[^\d+]/g, "");
  if (!trimmed.startsWith("+")) {
    return { dial: "", national: trimmed.replace(/\D/g, "") };
  }
  for (const id of DIAL_OPTIONS) {
    const dc = MARKETS[id].dialCode;
    if (dc && trimmed.startsWith("+" + dc)) {
      return { dial: dc, national: trimmed.slice(dc.length + 1).replace(/\D/g, "") };
    }
  }
  return { dial: "", national: trimmed.slice(1).replace(/\D/g, "") };
}

/** تجميل الرقم المحلّي بمسافات (3-3-4 أو حسب الطول) للعرض داخل الحقل */
export function prettyNational(raw: string): string {
  const d = raw.replace(/\D/g, "");
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
  return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 9)} ${d.slice(9)}`;
}

/** محفوظة للتوافق مع الاختبارات القديمة، لكنّ العرض في الحقل صار لاتينيّاً دائماً */
export function toArabicDigits(s: string): string {
  return s.replace(/\d/g, (d) => "٠١٢٣٤٥٦٧٨٩"[Number(d)]);
}

/** يحوّل أيّ أرقام (لاتينيّة/عربيّة-هنديّة/فارسيّة) إلى لاتينيّة */
export function toLatinDigits(s: string): string {
  return s
    .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[\u06f0-\u06f9]/g, (d) => String(d.charCodeAt(0) - 0x06f0));
}

export function PhoneInput({ value, onChange, className }: PhoneInputProps) {
  const { effectiveMarket, locale } = useLocale();
  const isAr = locale === "ar";

  const parsed = React.useMemo(() => parseE164(value), [value]);

  const defaultDial =
    parsed.dial ||
    (effectiveMarket && MARKETS[effectiveMarket as MarketId]?.dialCode) ||
    (MARKETS.AE.dialCode ?? "971");

  const [dial, setDial] = React.useState<string>(defaultDial);
  const [national, setNational] = React.useState<string>(parsed.national);

  React.useEffect(() => {
    if (parsed.dial) setDial(parsed.dial);
    setNational(parsed.national);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (newDial: string, newNational: string) => {
    const cleanedNat = newNational.replace(/\D/g, "").replace(/^0+/, "");
    onChange(cleanedNat ? "+" + newDial + cleanedNat : "");
  };

  // اعثر على marketId المطابق للـdial الحاليّ (للحصول على placeholder)
  const currentMarketId = (Object.keys(MARKETS) as MarketId[]).find(
    (id) => MARKETS[id].dialCode === dial,
  );
  const examplePlaceholder = currentMarketId ? EXAMPLES[currentMarketId] : "";

  const displayValue = prettyNational(national);

  return (
    <div className={"flex items-stretch gap-2 " + (className ?? "")} dir="ltr">
      <Select
        value={dial}
        onValueChange={(v) => {
          setDial(v);
          emit(v, national);
        }}
      >
        <SelectTrigger
          className="h-11 w-[125px] shrink-0 border border-border bg-background px-2 text-sm focus:border-primary"
          aria-label={isAr ? "رمز الدولة" : "country code"}
        >
          <SelectValue>
            <span className="flex items-center gap-1.5">
              <span className="text-base leading-none">
                {currentMarketId ? MARKETS[currentMarketId].flag : "🌐"}
              </span>
              <span className="font-mono tabular-nums" dir="ltr">
                +{dial}
              </span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {DIAL_OPTIONS.map((id) => {
            const m = MARKETS[id];
            if (!m.dialCode) return null;
            const label = m.labels[isAr ? "ar" : "en"] ?? m.labels.en;
            return (
              <SelectItem key={id} value={m.dialCode}>
                <span className="flex items-center gap-2">
                  <span className="text-base leading-none">{m.flag}</span>
                  <span className="font-mono tabular-nums" dir="ltr">
                    +{m.dialCode}
                  </span>
                  <span className="text-xs text-muted-foreground">{label}</span>
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>


      <input
        type="tel"
        inputMode="tel"
        lang="en"
        style={{ direction: "ltr", unicodeBidi: "plaintext" }}
        autoComplete="tel-national"
        className="h-11 w-full border border-border bg-background/40 px-3 text-base tabular-nums focus:border-primary focus:outline-none"
        value={displayValue}
        onChange={(e) => {
          let raw = toLatinDigits(e.target.value).replace(/\D/g, "");
          // اقتطاع الصفر البادئ (شائع في 05/07/09) قبل إضافة رمز الدولة
          raw = raw.replace(/^0+/, "");
          // حدّ أقصى حسب الدولة لمنع تجاوز الطول
          const maxLen = currentMarketId ? NATIONAL_LENGTHS[currentMarketId] ?? 15 : 15;
          if (maxLen > 0 && raw.length > maxLen) raw = raw.slice(0, maxLen);
          setNational(raw);
          emit(dial, raw);
        }}
        placeholder={examplePlaceholder}
        dir="ltr"
      />

    </div>
  );
}
